import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Rendered under the title and announced as the dialog's description. */
  description?: ReactNode;
  children?: ReactNode;
  /** Focused on open; defaults to the first focusable element in the box. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  showCloseButton?: boolean;
  titleClassName?: string;
  boxClassName?: string;
}

// A native <dialog> driven by `isOpen`. It names itself from its title, moves
// focus in on open and back to the opener on close, and reports every way the
// browser can close it (Escape, the backdrop, a `method="dialog"` form) through
// onClose — exactly once, since closing in response to `isOpen` stays silent.
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  initialFocusRef,
  showCloseButton = false,
  titleClassName = 'font-bold text-lg',
  boxClassName = '',
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isOpenRef = useRef(isOpen);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    isOpenRef.current = isOpen;
    if (isOpen) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      if (!dialog.open) dialog.showModal();
      const target = initialFocusRef?.current ?? dialog.querySelector<HTMLElement>(`.modal-box :is(${FOCUSABLE})`);
      target?.focus();
    } else {
      // Already closed if the browser closed it (Escape, backdrop) first.
      if (dialog.open) dialog.close();
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    }
  }, [isOpen, initialFocusRef]);

  const handleNativeClose = () => {
    if (isOpenRef.current) onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className="modal modal-bottom sm:modal-middle"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onClose={handleNativeClose}
    >
      <div className={`modal-box ${boxClassName}`}>
        <h2 id={titleId} className={titleClassName}>
          {title}
        </h2>
        {description && (
          <p id={descriptionId} className="py-4">
            {description}
          </p>
        )}
        {children}
        {/* Last in the DOM so default focus and Tab land on the content first. */}
        {showCloseButton && (
          <button
            type="button"
            className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        )}
      </div>
      <form method="dialog" className="modal-backdrop">
        <button tabIndex={-1}>close</button>
      </form>
    </dialog>
  );
}
